
import { Component, ComponentFactoryResolver, Input, TemplateRef, ViewChild, ViewContainerRef } from "@angular/core";
import { DxContextMenuComponent, DxDataGridComponent } from "devextreme-angular";
import { GridExtender } from "../grid/Grid.extender";
import { BaseDataSourceAbstract } from "../datalayer/BaseDataSourceAbstract";
import { BaseState, BaseEntity } from "./../entities/BaseEntities";
import { BaseComponent } from "./BaseComponent";
import { ServiceBagService } from "./ServiceBag";
import { BasePageComponent } from "./BasePageComponent";
import { GridToolbarComponent } from '@m4u-angular/ui/layouts/componentlayouts/grid-toolbar/grid-toolbar.component';
import { AppInjector } from '@m4u-angular/core/injector/injector.module';
import dxButton from 'devextreme/ui/button';
import { Column } from 'devextreme/ui/data_grid';
import { ContextMenuElement } from '../classes/BaseClasses';
import { Subscription } from 'rxjs';
import dxScrollView from 'devextreme/ui/scroll_view';

@Component({
    selector: 'base-grid',
    template: '<div></div>'
})
export abstract class BaseGridComponent<TEntity extends BaseEntity, TState extends BaseState> extends BaseComponent<TState> implements IGridComponent {
    private _gridToolbarComponent: GridToolbarComponent;
    private _subscriptions: Subscription[] = [];

    private _dataSource: BaseDataSourceAbstract | any[];
    @Input() set DataSource(value: BaseDataSourceAbstract | any[]) {
        this._dataSource = value;
        if (value) this.InitDataSource();
    }
    get DataSource() { return this._dataSource; }

    private _showAddButton: boolean;
    @Input() set ShowAddButton(value: boolean) {
        this._showAddButton = value;
        this._gridToolbarComponent && (this._gridToolbarComponent.ShowAddButton = this._showAddButton);
    }
    get ShowAddButton() { return this._showAddButton; }

    private _addButtonText: string;
    @Input() set AddButtonText(value: string) {
        this._addButtonText = value;
        this._gridToolbarComponent && (this._gridToolbarComponent.AddButtonText = this._addButtonText);
    }
    get AddButtonText() { return this._addButtonText; }

    private _title: string;
    @Input() set Title(value: string) {
        this._title = value;
        this._gridToolbarComponent && (this._gridToolbarComponent.Title = this._title);
    }
    get Title() { return this._title; }

    private _addButtonNavParams: any;
    @Input() set AddButtonNavParams(value: any) {
        this._addButtonNavParams = value;
        this._gridToolbarComponent && (this._gridToolbarComponent.AddButtonNavParams = this._addButtonNavParams);
    }
    get AddButtonNavParams() { return this._addButtonNavParams; }

    @Input() set Visible(value: boolean) {
        this.grid && (this.grid.visible = value);
    }

    @Input() IsAutoHeight: boolean;
    @Input() Page: BasePageComponent<BaseEntity, BaseState>;
    @Input() AddButtonNavPath: string;
    @Input() StartExtraButtonsTemplate: TemplateRef<any>;
    @Input() EndExtraButtonsTemplate: TemplateRef<any>;

    @ViewChild(DxDataGridComponent) protected grid: DxDataGridComponent;
    @ViewChild(DxDataGridComponent, { read: ViewContainerRef }) containerRef: ViewContainerRef;

    public get gridAny(): any { return this.grid; }
    public get G(): GridExtender { return this.gridAny && this.gridAny.G; }

    protected AfterInit() { }

    constructor(public override A: ServiceBagService,
        public override ComponentName: string,
        public EntityType: (new () => TEntity),
        StateType: (new () => TState)) {
        super(A, ComponentName, StateType);
    }

    protected override OnInitComponent(): void {
        this.A.SysEvents.SetLoadPanelState(true);
    }

    InitDataSource() {
        //if (this.DataSource instanceof BaseDataSourceAbstract) {
        //    this.DataSource.AddEventListener('loadingChanged', (isLoading: boolean) => {
        //        if (this.G && this.G.IsAutoResizingEnabled && !isLoading) {
        //            console.log('loadingChanged');
        //            this.RepaintGrid();
        //        }
        //    });
        //}

        this.AfterInit();
    }

    override OnInitComponentFinished() {
        this._gridToolbarComponent = this.CreateGridToolbarComponent();

        this._subscriptions.push(this.grid.onContentReady.subscribe(() => {
            setTimeout(() => this.RefreshGridHeight());
            // Get context menu items without putting data there, just to know if any exists
            const contextMenuItems = this.GetGridRowContextMenu();
            // If no context menu items then no need to add column at all
            if (!contextMenuItems || !contextMenuItems.length) return;

            // TODO: better to use addColumn API method.
            const columns = this.grid.instance.option('columns');
            const gridColumns = columns.map((column: Column) => ({
                ...column,
                visible: column.type === 'buttons' ? column.visible : this.grid.instance.columnOption(column.dataField, "visible"),
                width: column.type === 'buttons' ? column.width : this.grid.instance.columnOption(column.dataField, "width"),
                visibleIndex: column.type === 'buttons' ? column.visibleIndex : this.grid.instance.columnOption(column.dataField, "visibleIndex"),
                groupIndex: column.type === 'buttons' ? column.groupIndex : this.grid.instance.columnOption(column.dataField, "groupIndex")
            }));

            const gridRowContextMenuColumn = gridColumns.find(x => x.name == 'gridRowContextMenu');
            if (!gridRowContextMenuColumn) {
                const columnOptions = {
                    name: 'gridRowContextMenu',
                    showInColumnChooser: false,
                    type: 'buttons',
                    cellTemplate: this.GridRowContextMenu,
                    allowSearch: false,
                    cssClass: 'context-menu-column',
                    allowResizing: false,
                    allowReordering: false,
                    width: 32
                } as any;
                gridColumns.push(columnOptions);
                (this.grid.instance as any).option('columns', gridColumns);
            }
        }));

        if (Array.isArray(this.DataSource)) this.RepaintGrid();

        this.A.SysEvents.SetLoadPanelState(false);
    }

    GridRowContextMenu = (container, options) => {
        if (options.row.rowType !== 'data') return;

        const contextMenuItems = this.GetGridRowContextMenu(options);
        if (!contextMenuItems.length || !contextMenuItems.some(x => x.visible == null || x.visible)) return;

        const div = document.createElement("div");
        container.appendChild(div);

        const contextMenu = this.CreateContextMenuComponent();
        contextMenu.items = contextMenuItems;
        contextMenu.target = div;

        const contextMenuButton = new dxButton(div, {
            icon: 'mdi mdi-dots-vertical',
            elementAttr: { class: 'grid-row-context-menu-button btn-size-small' },
            onClick: (event) => {
                event.event?.stopPropagation();
                contextMenu.visible = true;
            }
        });
    };

    private CreateContextMenuComponent() {
        const componentFactoryResolver = AppInjector.get(ComponentFactoryResolver);
        const componentFactory = componentFactoryResolver.resolveComponentFactory(DxContextMenuComponent);
        const componentRef = this.containerRef.createComponent<DxContextMenuComponent>(componentFactory);

        return componentRef.instance;
    }

    // Please make sure that options?.row?.rowData or options?.data is using with '?' (for example, options?.row?.rowData?.EntryID) when overriding this method
    protected GetGridRowContextMenu(options?: any): ContextMenuElement[] {
        return [];
    }

    RefreshGridHeight() {
        if (this.grid && this.G && this.IsAutoHeight && this.G.GetRowCount() > 0 && this.grid.dataSource && !this.gridAny.instance._disposed) {
            const height = this.G.GetGridHeightRelativeToPage(this.Page);
            const currentHeight = this.gridAny.instance.option('height');
            if (height !== currentHeight) this.gridAny.instance.option('height', height);
        }
    }

    RepaintGrid() {
        /*console.log('repaint');
        // Scroll page to the top for correct grid height calculation
        const pageScrollElement = document.querySelector('.page-body.dx-scrollable');
        if (pageScrollElement) {
            const pageScroll = dxScrollView.getInstance(pageScrollElement) as any;
            pageScroll && pageScroll.scrollTo({ top: 0 });
        }

        // Disable user activity by load panel until gris height calculation
        this.A.SysEvents.SetLoadPanelState(true);
        //this is because of devextreme code... . See _updateDimensionsCore() in ui.grid_core.grid_view.js
        //that._hasHeight is NaN because of maxHeightHappened returns NaN
        //even more there are two variables hasHeight and _hasHeight
        //this.RefreshGridHeight() sets that._hasHeight = true, but hasHeight still NaN...
        //that is why we call this.grid.instance.resize() in RefreshGridHeight() which will set hasHeight = that._hasHeight where that._hasHeight == true
        setTimeout(() => {
            this.RefreshGridHeight();
            // Enable user activity
            this.A.SysEvents.SetLoadPanelState(false);
        }, 1000);*/
    }

    override Navigate(params: any[], optionalParams?: any, bSaveState: boolean = false, bSaveCurrentLocation = false): Promise<boolean> {
        if (this.Page != undefined) (this.Page as any).SaveState();
        return super.Navigate(params, optionalParams, bSaveState, bSaveCurrentLocation);
    }

    private CreateGridToolbarComponent(): GridToolbarComponent {
        if (!this.Title && !this.ShowAddButton && !this.StartExtraButtonsTemplate && !this.EndExtraButtonsTemplate) return;

        const componentFactoryResolver = AppInjector.get(ComponentFactoryResolver);
        const componentFactory = componentFactoryResolver.resolveComponentFactory(GridToolbarComponent);
        const componentRef = this.containerRef.createComponent<GridToolbarComponent>(componentFactory);

        componentRef.instance.Grid = this.grid;
        componentRef.instance.ShowAddButton = this.ShowAddButton;
        componentRef.instance.Title = this.Title;
        componentRef.instance.AddButtonNavPath = this.AddButtonNavPath;
        componentRef.instance.AddButtonNavParams = this.AddButtonNavParams;
        componentRef.instance.StartExtraButtonsTemplate = this.StartExtraButtonsTemplate;
        componentRef.instance.EndExtraButtonsTemplate = this.EndExtraButtonsTemplate;
        componentRef.instance.AddButtonText = this.AddButtonText;

        this.containerRef.element.nativeElement.parentElement.before(componentRef.location.nativeElement);

        return componentRef.instance;
    }

    protected OnReorder(e, lines) {
        const visibleRows = e.component.getVisibleRows();
        const toIndex = lines.indexOf(visibleRows[e.toIndex].data);
        const fromIndex = lines.indexOf(e.itemData);
        lines.splice(fromIndex, 1);
        lines.splice(toIndex, 0, e.itemData);
    }

    protected override OnDestroyComponent(): void {
        this._subscriptions.forEach(x => x.unsubscribe());
    }
}


export interface IGridComponent {
    readonly G: GridExtender;
}
